// Validation constants matching the API service layer
export const MAX_BIO_LENGTH = 500
export const MAX_DISPLAY_NAME_LENGTH = 50
export const MAX_LOCATION_LENGTH = 128
export const MAX_USER_TAGS = 5

export const PROFILE_VISIBILITY_VALUES = ['public', 'private']
export const FIELD_VISIBILITY_VALUES = ['public', 'private']
export const PLAYLIST_VISIBILITY_VALUES = ['ANONYMOUS', 'PUBLIC', 'PRIVATE']

/**
 * Check if entire profile should be visible to the viewer
 */
export function isProfileVisible ({ profileVisibility, visibilityLevel }: {
  profileVisibility: string
  visibilityLevel: string
}): boolean {
  if (visibilityLevel === 'owner') return true
  if (profileVisibility === 'private') return false
  if (profileVisibility === 'membersOnly' && visibilityLevel === 'public') return false
  return true
}

/**
 * Check if a single field should be visible to the viewer.
 *
 * `fieldVisibility` may be `null`/`undefined` for non-owner viewers because the
 * backend hides *Visibility settings from non-owners (the visibility _setting_
 * is owner-private metadata; the _data_ is independently gated and nulled out
 * by the backend when hidden). Null/undefined therefore means "no restriction
 * known to this viewer" and should not hide data the backend already decided
 * to send.
 */
export function isFieldVisible (
  fieldVisibility: string | null | undefined,
  visibilityLevel: string,
): boolean {
  if (visibilityLevel === 'owner') return true
  if (fieldVisibility === 'private') return false
  if (fieldVisibility === 'membersOnly' && visibilityLevel === 'public') return false
  return true
}

/**
 * Get allowed playlist visibility options based on profile visibility.
 * Private profiles cannot have PUBLIC playlists.
 */
export function getAllowedPlaylistVisibilities (profileVisibility: string): string[] {
  if (profileVisibility === 'private') return ['ANONYMOUS', 'PRIVATE']
  return ['PUBLIC', 'ANONYMOUS', 'PRIVATE']
}

/**
 * Get a friendly label for a visibility setting
 */
export function getVisibilityLabel (visibility: string): string {
  switch (visibility) {
    case 'public': return 'Public'
    case 'membersOnly': return 'Members only'
    case 'private': return 'Private'
    default: return ''
  }
}

/**
 * Resolved labels for the two system playlists. React-tree callers should
 * resolve these via `useTranslatedText` against their enclosing slice (every
 * Community static-text slice that needs them — `communityMyLibraryPage`,
 * `communityProfilePage`, `playlistSharedView` — has both keys).
 */
export interface SystemPlaylistNameLookup {
  watchLater: string
  favorites: string
}

/**
 * Resolve the display name for a playlist. System playlists (Watch Later,
 * Favorites) are stored with system identifiers in `playlistName` (e.g.
 * `'default'`), so we translate them by `playlistType`. Custom playlists
 * fall through to `playlistName`.
 *
 * Pass `names` from the React-tree caller (resolved via `useTranslatedText`).
 * Non-React callers (memoized comparators, SSR-time string ops, document.title
 * payloads) may omit `names`; the function falls back to English so the
 * surface still renders a recognizable label. (The same English-fallback
 * exception is used by aria-label resolvers elsewhere in the app.)
 */
export function getPlaylistDisplayName (
  playlist: { playlistType?: string | null; playlistName?: string | null } | null | undefined,
  names?: SystemPlaylistNameLookup | null,
): string {
  if (!playlist) return ''
  if (playlist.playlistType === 'default') {
    return names?.watchLater ?? 'Watch Later'
  }
  if (playlist.playlistType === 'favorites') {
    return names?.favorites ?? 'Favorites'
  }
  return playlist.playlistName || ''
}
